import React, { useState, useCallback } from 'react';
import {
    Box,
    Typography,
    Button,
    Stack,
    Paper,
    Menu,
    MenuItem,
    TextField,
    Divider,
    Chip,
    InputAdornment,
    CircularProgress,
    Card
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import SearchIcon from '@mui/icons-material/Search';
import { PartyProps, PartySelectSectionProps } from '../../../types';
import { toast } from 'react-toastify';
import { useFormContext } from 'react-hook-form';

// Updated props interface
interface PartySelectSectionWithFormProps extends Omit<PartySelectSectionProps, 'selectedParty' | 'onPartySelect'> {
    request: (options: { url: string; method: string; params?: any }) => Promise<any>;
    apiParams?: Record<string, any>;
    // React Hook Form integration props
    formName?: string; // For form field registration
    // Only send party ID in payload
    sendPartyIdOnly: true; // Always true since we only want ID
}

const PartySelectSection: React.FC<PartySelectSectionWithFormProps> = ({
    billToLabel = 'Bill To',
    parties = [],
    billToAddress,
    onBillToAddressChange,
    onAddNewParty,
    loading = false,
    error,
    compact = false,
    sx = {},
    request,
    apiParams = {},
    formName,
    sendPartyIdOnly = true,
    showTotalDue = false,
    totalDue
}) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [apiParties, setApiParties] = useState<PartyProps[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [apiError, setApiError] = useState<string | null>(null);

    // React Hook Form integration
    const { setValue, getValues, watch } = useFormContext?.() || {};

    // Get selected party from form
    const formParty = formName ? watch?.(formName) : null;

    // Find full party details for display when we only have ID
    const findPartyDetails = (partyId: string): PartyProps | null => {
        return apiParties.find(p => p._id === partyId) || null;
    };

    // Get party details for display
    const getDisplayPartyDetails = () => {
        if (!formParty) return null;

        // If formParty is just a string (ID), find the full details
        if (typeof formParty === 'string') {
            return findPartyDetails(formParty);
        }

        // If formParty is a PartyRef with _id, find full details
        if (formParty && typeof formParty === 'object' && '_id' in formParty) {
            return findPartyDetails(formParty._id);
        }

        return null;
    };

    const partyDetails = getDisplayPartyDetails();

    // Fetch parties from API when menu opens
    const fetchParties = useCallback(async () => {
        setIsLoading(true);
        setApiError(null);

        try {
            const params = new URLSearchParams();

            Object.entries(apiParams).forEach(([key, value]) => {
                if (value !== undefined && value !== null) {
                    params.append(key, value.toString());
                }
            });

            if (searchTerm.trim()) {
                params.append('search', searchTerm.trim());
            }

            const url = `party/get-all-parties?${params.toString()}`;

            const res = await request({
                url: url,
                method: 'get',
            });

            if (res.status === "success" && res.parties) {
                setApiParties(res.parties);
            } else {
                setApiError(res.message || 'Failed to fetch parties');
                setApiParties(parties);
            }
        } catch (error: any) {
            toast.error('Error fetching parties:', error);
            setApiError('Failed to load parties');
            setApiParties(parties);
        } finally {
            setIsLoading(false);
        }
    }, [request, apiParams, searchTerm, parties]);

    // Filter parties based on search locally
    const filteredParties = searchTerm.trim()
        ? apiParties.filter(party =>
            party.partyName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            party.contact?.phone?.[0]?.phoneNo?.includes(searchTerm) ||
            party.contact?.email?.[0]?.toLowerCase().includes(searchTerm.toLowerCase())
        )
        : apiParties;

    const handleAddClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
        await fetchParties();
    };

    const handleClose = () => {
        setAnchorEl(null);
        setSearchTerm('');
        setApiError(null);
    };

    const handlePartySelect = (party: PartyProps) => {
        // ALWAYS send only party ID in payload
        const partyPayload: string = party._id; // Just the string ID

        // Update form with only the ID string
        if (formName && setValue) {
            setValue(formName, partyPayload); // Store just the ID string
            // setValue(`${formName}BillAddress`, party.billingAddress || party.address?.line1 || '');
            setValue(`${formName}Name`, party.partyName); // Store name separately if needed
        }

        handleClose();
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value);
    };

    // Function to get address from form or prop
    const getAddress = () => {
        if (formName) {
            const billAddr = getValues?.(`${formName}BillAddress`);
            return billAddr;
        }
        return billToAddress;
    };

    return (
        <Card sx={{ width: '100%', height: "100%", p: 1, ...sx }}>
            {/* Compact Professional Design */}
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5
            }}>
                {/* Bill To Label */}
                <Typography variant="subtitle2" fontWeight="bold" color="text.primary">
                    {billToLabel}
                </Typography>

                {/* Select Party Section */}
                {!partyDetails ? (
                    <Paper
                        variant="outlined"
                        sx={{
                            p: 0,
                            borderRadius: 1,
                            borderStyle: 'dashed',
                            borderColor: 'grey.300',
                            bgcolor: 'grey.50',
                            '&:hover': {
                                borderColor: 'primary.main',
                                bgcolor: 'primary.50'
                            }
                        }}
                    >
                        <Button
                            startIcon={<AddIcon />}
                            onClick={handleAddClick}
                            variant="text"
                            size="small"
                            sx={{
                                p: 2,
                                textTransform: 'none',
                                color: 'text.secondary',
                                '&:hover': {
                                    color: 'primary.main'
                                }
                            }}
                            disabled={loading}
                            fullWidth
                        >
                            {loading ? 'Loading...' : `Select ${billToLabel}`}
                        </Button>
                    </Paper>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {/* Selected Party Card - Compact Design */}
                        <Paper
                            variant="outlined"
                            sx={{
                                p: 1.5,
                                borderRadius: 1,
                                borderColor: 'primary.light',
                                bgcolor: 'primary.50',
                                position: 'relative'
                            }}
                        >
                            {/* Change Party Button */}
                            <Button onClick={handleAddClick}
                                size='small'
                                sx={{
                                    px: 1,
                                    fontSize: "10px",
                                    position: 'absolute',
                                    top: 8,
                                    right: 8,
                                    bgcolor: 'white',
                                    border: '1px solid',
                                    borderColor: 'primary.light',
                                    '&:hover': {
                                        bgcolor: 'primary.50'
                                    }
                                }}>Change Party </Button>

                            {/* Party Info */}
                            <Box sx={{ pr: 4 }}>
                                {/* Party Name and Type */}
                                <Box display="flex" alignItems="center" gap={1} mb={1}>
                                    {partyDetails.entityCategory === 'PARTY' ? (
                                        <BusinessIcon color="primary" fontSize="small" />
                                    ) : (
                                        <PersonIcon color="primary" fontSize="small" />
                                    )}
                                    <Typography variant="body2" fontWeight="bold" noWrap>
                                        {partyDetails.partyName}
                                    </Typography>
                                    <Chip
                                        label={partyDetails.entityCategory === 'PARTY' ? 'Party' : 'Walk In'}
                                        size="small"
                                        color="primary"
                                        variant="outlined"
                                        sx={{ height: 20, fontSize: '0.65rem' }}
                                    />
                                </Box>

                                {/* Contact Info */}
                                <Stack spacing={0.5}>
                                    {partyDetails.contact?.phone?.[0]?.phoneNo && (
                                        <Typography variant="caption" display="flex" alignItems="center" gap={0.5}>
                                            <PhoneIcon fontSize="inherit" sx={{ opacity: 0.7 }} />
                                            {partyDetails.contact.phone[0].phoneNo}
                                        </Typography>
                                    )}

                                    {partyDetails.contact?.email?.[0] && (
                                        <Typography variant="caption" display="flex" alignItems="center" gap={0.5}>
                                            <EmailIcon fontSize="inherit" sx={{ opacity: 0.7 }} />
                                            {partyDetails.contact.email[0]}
                                        </Typography>
                                    )}

                                    {partyDetails.gstNumber && (
                                        <Typography variant="caption" fontWeight="medium" color="primary">
                                            GST: {partyDetails.gstNumber}
                                        </Typography>
                                    )}
                                </Stack>
                            </Box>
                        </Paper>

                        {/* Billing Address - Compact */}
                        <Box>
                            <Typography variant="caption" fontWeight="medium" color="text.secondary" display="block" mb={0.5}>
                                Billing Address
                            </Typography>
                            <Paper
                                variant="outlined"
                                sx={{
                                    p: 1,
                                    borderRadius: 1,
                                    bgcolor: 'grey.50'
                                }}
                            >
                                <Box display="flex" alignItems="flex-start" gap={0.5}>
                                    <LocationOnIcon color="action" fontSize="small" sx={{ mt: '2px' }} />
                                    <Typography variant="caption" sx={{ flex: 1, lineHeight: 1.4 }}>
                                        {getAddress() ||
                                            partyDetails.billingAddress ||
                                            partyDetails.location ||
                                            partyDetails.address?.line1 ||
                                            'No billing address provided'
                                        }
                                    </Typography>
                                </Box>
                            </Paper>
                        </Box>

                        {/* Total Due Display */}
                        {showTotalDue ? (
                            <Card sx={{ mt: 0, p: 1.5, bgcolor: 'info.50' , display:"flex", alignItems:'center', gap:1}}>
                                <Typography variant="caption" color="text.secondary" fontWeight='bold'>
                                    Total Outstanding :
                                </Typography>
                                <Typography variant="h6" color="error.main" fontWeight={600}>
                                    ₹{totalDue}
                                </Typography>
                            </Card>
                        ) : ''}
                        {/* {totalDueAmount} */}
                    </Box>
                )}
            </Box>

            {/* Error Message */}
            {(error || apiError) && (
                <Typography variant="caption" color="error" mt={1} display="block">
                    {error || apiError}
                </Typography>
            )}

            {/* Parties Dropdown Menu */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                PaperProps={{
                    sx: {
                        maxHeight: 400,
                        width: 350,
                        mt: 0.5,
                        boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)'
                    }
                }}
            >
                {/* Search Bar */}
                <Box sx={{ p: 1, borderBottom: 1, borderColor: 'divider' }}>
                    <TextField
                        placeholder="Search parties..."
                        value={searchTerm}
                        onChange={handleSearchChange}
                        size="small"
                        fullWidth
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                            sx: { fontSize: '0.875rem' }
                        }}
                        autoFocus
                    />
                </Box>

                {/* Loading State */}
                {isLoading && (
                    <Box display="flex" justifyContent="center" p={2}>
                        <CircularProgress size={20} />
                    </Box>
                )}

                {/* Parties List */}
                {!isLoading && (
                    <Box sx={{ maxHeight: 250, overflow: 'auto' }}>
                        {filteredParties.length === 0 ? (
                            <MenuItem disabled sx={{ py: 2 }}>
                                <Typography variant="body2" color="text.secondary" textAlign="center" width="100%">
                                    {apiError ? 'Error loading parties' : 'No parties found'}
                                </Typography>
                            </MenuItem>
                        ) : (
                            filteredParties.map((party) => (
                                <MenuItem
                                    key={party._id}
                                    onClick={() => handlePartySelect(party)}
                                    sx={{
                                        py: 1.25,
                                        px: 1.5,
                                        borderBottom: '1px solid',
                                        borderColor: 'divider',
                                        '&:last-child': { borderBottom: 'none' },
                                        '&:hover': { bgcolor: 'primary.50' }
                                    }}
                                >
                                    <Box sx={{ width: '100%' }}>
                                        {/* Party Name and Type */}
                                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                                            <Typography variant="body2" fontWeight="bold" noWrap>
                                                {party.partyName || 'Unnamed Party'}
                                            </Typography>
                                            <Chip
                                                label={party.entityCategory === 'PARTY' ? 'Party' : 'Walk In'}
                                                size="small"
                                                color="primary"
                                                variant="outlined"
                                                sx={{ height: 20, fontSize: '0.7rem' }}
                                            />
                                        </Box>

                                        {/* Contact Info */}
                                        <Stack spacing={0.25}>
                                            {party.contact?.phone?.[0]?.phoneNo && (
                                                <Typography variant="caption" color="text.secondary" display="flex" alignItems="center" gap={0.5}>
                                                    <PhoneIcon fontSize="inherit" sx={{ opacity: 0.7 }} />
                                                    {party.contact.phone[0].phoneNo}
                                                </Typography>
                                            )}

                                            {party.contact?.email?.[0] && (
                                                <Typography variant="caption" color="text.secondary" display="flex" alignItems="center" gap={0.5}>
                                                    <EmailIcon fontSize="inherit" sx={{ opacity: 0.7 }} />
                                                    {party.contact.email[0]}
                                                </Typography>
                                            )}

                                            {party.address?.line1 && (
                                                <Typography variant="caption" color="text.secondary" display="flex" alignItems="center" gap={0.5}>
                                                    <LocationOnIcon fontSize="inherit" sx={{ opacity: 0.7 }} />
                                                    {party.address.line1}
                                                </Typography>
                                            )}
                                        </Stack>
                                    </Box>
                                </MenuItem>
                            ))
                        )}
                    </Box>
                )}

                {/* Add New Party Button */}
                <Divider />
                <Box sx={{ p: 1 }}>
                    <Button
                        startIcon={<AddIcon />}
                        onClick={() => {
                            handleClose();
                            if (onAddNewParty) onAddNewParty();
                        }}
                        variant="contained"
                        fullWidth
                        size="small"
                        sx={{
                            textTransform: 'none',
                            fontSize: '0.875rem',
                            py: 0.75
                        }}
                    >
                        Add New Party
                    </Button>
                </Box>
            </Menu>
        </Card>
    );
};


export default PartySelectSection;